import axios, { AxiosError } from "axios";
import { useCallback, useEffect } from "react";

import { getTenantHeaders } from "../helpers/tenantUtils";
import { UserInfo } from "../types/domain/User";

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  success: boolean;
  error: any;
}

interface Tokens {
  token?: string;
  refreshToken?: string;
}

declare global {
  interface Window {
    currentRefreshRequest: Promise<Tokens | void>;
    isTokenRefreshing: boolean;
    lastUsedRefreshToken: string;
  }
}

export const refreshTokenRequest = async (
  refreshTokenUrl: string,
  token: string,
  withCredentials: boolean,
  clientId?: string,
  refreshTokenBaseUrl?: string
): Promise<Tokens> => {

  const tenantHeader = getTenantHeaders();
  
  const identityHostApi = refreshTokenBaseUrl 
    ? axios.create({ baseURL: refreshTokenBaseUrl })
    : axios;

  const {
    data: { accessToken, refreshToken },
  } = await identityHostApi.post<TokenResponse>(
    refreshTokenUrl,
    {
      refreshToken: token,
      ...(clientId && { clientId }),
    },
    { withCredentials, headers: { ...tenantHeader } }
  );

  return {
    token: accessToken,
    refreshToken,
  };
};

const refreshTokenWithResend = async (
  error: AxiosError,
  refreshTokenUrl: string,
  helpers: {
    updateTokens: (tokens: Tokens) => void;
    refreshToken?: string;
    logout: () => void;
    isWithCredentials: boolean;
  },
  clientId?: string,
  refreshTokenBaseUrl?: string
) => {
  const {
    refreshToken = "",
    updateTokens,
    logout,
    isWithCredentials,
  } = helpers;

  if (
    !window.isTokenRefreshing &&
    refreshToken !== window.lastUsedRefreshToken
  ) {
    delete axios.defaults.headers.Authorization;
    window.isTokenRefreshing = true;
    window.lastUsedRefreshToken = refreshToken;
    window.currentRefreshRequest = refreshTokenRequest(
      refreshTokenUrl,
      refreshToken,
      isWithCredentials,
      clientId,
      refreshTokenBaseUrl
    )
      .then((res: Tokens) => {
        if (res.token) {
          updateTokens(res);
          return res;
        }
        throw new Error("Refresh token failed");
      })
      .catch(logout)
      .finally(() => {
        window.isTokenRefreshing = false;
      });
  }

  const tokens = await window.currentRefreshRequest;

  if (tokens && tokens.token && error.config) {
    error.config.headers = error.config.headers || {};
    error.config.headers.Authorization = `Bearer ${tokens.token}`;
    return axios(error.config);
  }

  return Promise.reject(error);
};

const useAccessRejectedInterceptor = (
  user: UserInfo | null | undefined,
  setData: (data: any) => void,
  refreshTokenUrl?: string,
  isWithCredentials = false,
  clientId?: string,
  refreshTokenBaseUrl?: string
) => {
  const updateTokens = useCallback(
    (tokens: Tokens) => {
      setData({
        ...user,
        ...tokens,
      });
    },
    [user, setData]
  );

  const logout = useCallback(() => setData(null), [setData]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const isAccessRejected = error?.response?.status === 401;

        if (!user) {
          return Promise.reject(error);
        }

        if (isAccessRejected && refreshTokenUrl) {
          return refreshTokenWithResend(error, refreshTokenUrl, {
            updateTokens,
            refreshToken: user?.refreshToken,
            logout,
            isWithCredentials
          }, 
          clientId,
          refreshTokenBaseUrl);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [logout, updateTokens, user, refreshTokenUrl, isWithCredentials, clientId, refreshTokenBaseUrl]);
};

export default useAccessRejectedInterceptor;
